import { useCallback, useState } from 'react';
import type { ZodType } from 'zod';

type Errors<T> = Partial<Record<keyof T, string>>;

interface UseZodFormReturn<T extends Record<string, unknown>> {
  values: T;
  errors: Errors<T>;
  setField: <K extends keyof T>(key: K, value: T[K]) => void;
  /** 전체 검증. 통과 시 파싱된 값 반환, 실패 시 null + errors 세팅 */
  validate: () => T | null;
  setValues: (next: T) => void;
  reset: (next?: T) => void;
}

/**
 * zod 스키마 기반 경량 폼 훅.
 *
 * 이 프로젝트는 react-hook-form 을 쓰지 않고(의존성 미존재) auth feature 처럼
 * zod + 수동 상태 패턴을 표준으로 한다. 그 패턴을 재사용 가능한 훅으로 추출.
 *
 * - 필드 변경 시 해당 필드 에러만 제거(즉각 피드백).
 * - validate() 는 제출 시 1회 호출 — 전체 스키마 검증.
 */
export function useZodForm<T extends Record<string, unknown>>(
  schema: ZodType<T>,
  initial: T,
): UseZodFormReturn<T> {
  const [values, setValuesState] = useState<T>(initial);
  const [errors, setErrors] = useState<Errors<T>>({});

  const setField = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setValuesState((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const validate = useCallback((): T | null => {
    const result = schema.safeParse(values);
    if (result.success) {
      setErrors({});
      return result.data;
    }
    const fieldErrors: Errors<T> = {};
    for (const issue of result.error.issues) {
      const key = issue.path[0] as keyof T | undefined;
      if (key && !fieldErrors[key]) fieldErrors[key] = issue.message;
    }
    setErrors(fieldErrors);
    return null;
  }, [schema, values]);

  const setValues = useCallback((next: T) => setValuesState(next), []);

  const reset = useCallback(
    (next?: T) => {
      setValuesState(next ?? initial);
      setErrors({});
    },
    [initial],
  );

  return { values, errors, setField, validate, setValues, reset };
}
